import { useEffect } from 'react'
import { usePdfImport } from '../../hooks/usePdfImport'
import { useTranslation } from '../../contexts/LanguageContext'
import { Modal } from '../ui/Modal'
import { PdfUploader } from './PdfUploader'
import { ImportProgress } from './ImportProgress'
import { PdfReviewModal } from './PdfReviewModal'
import toast from 'react-hot-toast'

interface ImportModalProps {
  isOpen: boolean
  onClose: () => void
  onImportComplete?: (cv: import('../../types/cv').CV, title: string) => void
}

export function ImportModal({ isOpen, onClose, onImportComplete }: ImportModalProps) {
  const { t } = useTranslation()
  const {
    step,
    progress,
    error,
    extractedCV,
    usedAI,
    importPdf,
    reset,
    getImportedCV,
  } = usePdfImport()

  // Reset when modal opens
  useEffect(() => {
    if (isOpen) {
      reset()
    }
  }, [isOpen, reset])

  const handleFileSelect = async (file: File) => {
    await importPdf(file)
  }

  const handleConfirmImport = async () => {
    const importedCV = getImportedCV()
    if (!importedCV) return

    const title = importedCV.personalInfo?.fullName || t.import.importedTitle

    toast.success(t.import.importedSuccess)
    onClose()

    // Pass the imported CV to the parent component to handle adding to the list
    onImportComplete?.(importedCV, title)
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const getStatusText = () => {
    if (progress < 30) return t.import.readingPdf
    if (progress < 50) return t.import.extractingText
    if (progress < 90) return t.import.analyzingAI
    if (progress < 100) return t.import.finishing
    return t.import.completed
  }

  // Determine modal size based on step
  const modalSize = step === 'review' ? 'lg' : 'md'

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size={modalSize} noPadding={step === 'review'}>
      <div className={step === 'review' ? 'flex flex-col min-h-0 flex-1' : ''}>
        {/* Header — shown in all steps except review (which has its own) */}
        {step !== 'review' && (
          <div className="text-center mb-5">
            <div className="w-12 h-12 mx-auto mb-3 bg-primary/10 rounded-2xl flex items-center justify-center">
              <svg className="w-6 h-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
              </svg>
            </div>
            <h2 className="font-display text-xl font-bold text-gray-900">
              {t.import.title}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {t.import.subtitle}
            </p>
          </div>
        )}

        {/* Upload step */}
        {step === 'upload' && (
          <>
            <PdfUploader onFileSelect={handleFileSelect} />

            {/* Tips */}
            <div className="mt-5 rounded-xl border border-gray-100 p-4">
              <h3 className="font-display font-semibold text-sm text-gray-900 mb-2.5">
                {t.import.tipsTitle}
              </h3>
              <ul className="space-y-2 text-xs text-gray-500">
                {[t.import.tipSelectable, t.import.tipStandard, t.import.tipEditable].map((tip, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <svg className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}

        {/* Processing step */}
        {(step === 'parsing' || step === 'analyzing') && (
          <div className="py-6">
            <ImportProgress progress={progress} status={getStatusText()} />
          </div>
        )}

        {/* Error step */}
        {step === 'error' && (
          <div className="text-center py-8">
            <div className="w-14 h-14 mx-auto mb-3 bg-red-50 rounded-2xl flex items-center justify-center">
              <svg className="w-7 h-7 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
            </div>
            <h3 className="font-display text-lg font-semibold text-gray-900 mb-2">
              {t.import.errorTitle}
            </h3>
            <p className="text-sm text-gray-500 mb-6">{error}</p>
            <button
              onClick={reset}
              className="px-6 py-2.5 text-sm font-semibold text-white bg-primary rounded-xl hover:opacity-90 transition-opacity"
            >
              {t.common.retry}
            </button>
          </div>
        )}

        {/* Review step */}
        {step === 'review' && extractedCV && (
          <PdfReviewModal
            extractedCV={extractedCV}
            onConfirm={handleConfirmImport}
            onCancel={reset}
            usedAI={usedAI}
          />
        )}
      </div>
    </Modal>
  )
}
